import { Utils, StringUtils, ArrayUtils, Logger, engineComponent } from "neo-common";
import { ComponentTypes } from "../../ComponentTypes";
import { StageController } from "../../StageController";
import { Tweens, ITweenable, TweensAttributes } from "../../Effects/Tweens";
import { IElement } from "../Interfaces/IElement";
import { ShapeElement } from "./ShapeElement";
import { Attributes } from "../../../Utils/Attributes";
import { TextElement } from "../Texts/TextElement";
import { CustomUtils } from "../../../Utils/CustomUtils";

/**
 * ImageElement class is class which creates holder for image from resources. This image will be rendered to canvas.
 * Example:
 *   <ImageElement Class="ImageElement" Image="[imageId]" X="0" Y="0" />
 * @class ImageElement
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ImageElement extends PIXI.Sprite implements IElement, ITweenable {
    static DynamicTint: string = "ff0000";

    private _uid: string;
    private _cache: boolean;
    private _cloned: boolean;
    private __destroyed: boolean;
    private _tweenEngine: string;

    private maskImage: ImageElement;
    private maskImageName: string;
    maskSprite: PIXI.Sprite;

    usedInPool: boolean;

    static _destroyOptions: PIXI.DestroyOptions = {
        children: true,
    };

    zIndex: number = 0;

    constructor(texture?: PIXI.Texture) {
        super(texture);
        this._cache = false;
        this.usedInPool = false;
        this._uid = `${this.componentName}_${Utils.generateUuid()}`;

        this.tint = 0xffffff;

        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                this._uid = PerfDebug.generateElementUID(this);
                PerfDebug.newElement(this, this.getDebugInfo());
            }
        }
    }

    get tweenEngine(): string {
        return this._tweenEngine;
    }

    set tweenEngine(engine: string) {
        this._tweenEngine = engine;
    }

    public create(node: Element): void {
        this.createByNode(node);
        this.updateByNode(node);
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    createByNode(node: Element): void {
        if (DEBUG) {
            if (
                node.hasAttribute(ImageElementAttributes.DEBUG) &&
                node.getAttribute(ImageElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        // TODO only when creating new
        // Tweens.removeTweens(this);
        Tweens.removeTweens(this, "alpha");
        Tweens.removeTweens(this, "position");
        Tweens.removeTweens(this, "rotation");
        Tweens.removeTweens(this, "scale");
        Tweens.removeTweens(this, "skew");

        if (this.name == null) {
            this.name = node.nodeName;
        }
        //this.cacheAsBitmap = false;

        if (node.hasAttribute(ImageElementAttributes.IMAGE)) {
            // get loaded image from resources
            this.texture = StageController.getTextureByResourceId(node.getAttribute(ImageElementAttributes.IMAGE));
        } else if (node.hasAttribute(ImageElementAttributes.FROM_FRAME)) {
            if (node.hasAttribute(ImageElementAttributes.FRAME)) {
                let frame = parseInt(node.getAttribute(ImageElementAttributes.FRAME));
                let frameStr = frame < 10 ? "0" + frame : frame;
                this.texture = StageController.getTextureByResourceId(
                    node.getAttribute(ImageElementAttributes.FROM_FRAME) + "00" + frameStr,
                );
            } else {
                this.texture = StageController.getTextureByResourceId(
                    node.getAttribute(ImageElementAttributes.FROM_FRAME),
                );
            }
        } else if (node.hasAttribute(ImageElementAttributes.ANIMATION)) {
            let frame: number = 0;
            if (node.hasAttribute(ImageElementAttributes.FRAME)) {
                frame = parseInt(node.getAttribute(ImageElementAttributes.FRAME));
            }
            let frameStr = frame < 10 ? "0" + frame : frame;

            this.texture = StageController.getTextureByResourceId(
                node.getAttribute(ImageElementAttributes.ANIMATION) + "00" + frameStr,
            );
        }

        if (this.texture) {
            if (node.hasAttribute(ImageElementAttributes.ANCHOR)) {
                let anchorPoints = node.getAttribute(ImageElementAttributes.ANCHOR).split(";");

                if (anchorPoints.length === 1) {
                    this.anchor.x = parseFloat(anchorPoints[0]);
                    this.anchor.y = parseFloat(anchorPoints[0]);
                } else if (anchorPoints.length === 2) {
                    this.anchor.x = parseFloat(anchorPoints[0]);
                    this.anchor.y = parseFloat(anchorPoints[1]);
                }
            }
        } else {
            if (DEBUG) {
                if (!(this.texture && this.texture.baseTexture)) {
                    //this.log('ERROR no Image with name: ' + node.getAttribute(ImageElementAttributes.IMAGE));
                    this.log("ERROR Image has no texture");
                }
            }
        }

        if (node.hasAttribute(ImageElementAttributes.TWEEN_ENGINE)) {
            this._tweenEngine = node.getAttribute(ImageElementAttributes.TWEEN_ENGINE);
        }

        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.createElement(this, this.getDebugInfo());
            }
        }

        this.updateByNode(node);
    }

    /**
     * @param node
     */
    public updateByNode(node: Element): ImageElement {
        if (!node) {
            this.logError("No node specified for element update!");
            return;
        }

        if (DEBUG) {
            if (
                node.hasAttribute(ImageElementAttributes.DEBUG) &&
                node.getAttribute(ImageElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        if (
            !node.hasAttribute(ImageElementAttributes.TWEEN_PERSISTENT) ||
            StringUtils.parseBoolean(node.getAttribute(ImageElementAttributes.TWEEN_PERSISTENT)) != true
        ) {
            Tweens.removeTweens(this);
        }

        this._cache = Attributes.assignBooleanAttribute(node, ImageElementAttributes.CACHE, this._cache);
        this.visible = Attributes.assignBooleanAttribute(node, ImageElementAttributes.VISIBLE, this.visible);
        this.alpha = Attributes.assignFloatAttribute(node, ImageElementAttributes.ALPHA, this.alpha);
        this.blendMode = CustomUtils.getBlendMode(
            this.getBlendModeByString(
                Attributes.assignStringAttribute(node, ImageElementAttributes.BLEND_MODE, this.blendMode),
            ),
        );
        this.pivot.x = Attributes.assignIntAttribute(node, ImageElementAttributes.PIVOT_X, this.pivot.x);
        this.pivot.y = Attributes.assignIntAttribute(node, ImageElementAttributes.PIVOT_Y, this.pivot.y);

        if (node.hasAttribute(ImageElementAttributes.ROTATION)) {
            this.rotation = this.degrees2radians(parseFloat(node.getAttribute(ImageElementAttributes.ROTATION)));
        }

        if (node.hasAttribute(ImageElementAttributes.START_ROTATION)) {
            if (node.getAttribute(ImageElementAttributes.START_ROTATION) === ImageElementAttributes.START_ROTATION) {
                this.rotation = Math.floor(Math.random() * 360) / 100;
            }
        }

        this.position.x = Attributes.assignIntAttribute(node, ImageElementAttributes.X, this.position.x);
        this.position.y = Attributes.assignIntAttribute(node, ImageElementAttributes.Y, this.position.y);

        if (node.hasAttribute(ImageElementAttributes.SCALE_X)) {
            this.scale.x = parseFloat(node.getAttribute(ImageElementAttributes.SCALE_X));
        }

        if (node.hasAttribute(ImageElementAttributes.SCALE_Y)) {
            this.scale.y = parseFloat(node.getAttribute(ImageElementAttributes.SCALE_Y));
        }

        if (node.hasAttribute(ImageElementAttributes.SKEW_X)) {
            this.skew.x = this.degrees2radians(parseFloat(node.getAttribute(ImageElementAttributes.SKEW_X)));
        }

        if (node.hasAttribute(ImageElementAttributes.SKEW_Y)) {
            this.skew.y = this.degrees2radians(parseFloat(node.getAttribute(ImageElementAttributes.SKEW_Y)));
        }

        if (node.hasAttribute(ImageElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(ImageElementAttributes.SCALE));
            this.scale.y = parseFloat(node.getAttribute(ImageElementAttributes.SCALE));
        }

        if (node.hasAttribute(ImageElementAttributes.CLEAR_MASK)) {
            if (this.maskSprite) {
                this.removeChild(this.maskSprite);
                this.maskSprite = null;
            }
            if (this.maskImage) {
                this.removeChild(this.maskImage);
                this.maskImage = null;
            }
            if (this.mask) {
                this.mask = null;
            }
        }

        if (node.hasAttribute(ImageElementAttributes.MASK)) {
            let next: Element = node.parentElement.firstElementChild;
            while (next != null) {
                if (next.nodeName === node.getAttribute(ImageElementAttributes.MASK)) {
                    let doNotHide: boolean = next.getAttribute(ImageElementAttributes.SHOW_MASK) == "true";
                    if (this.mask && this.maskImage) {
                        next.setAttribute("Visible", "true");
                        this.maskImage.updateByNode(next);
                        if (!doNotHide) {
                            next.setAttribute("Visible", "false");
                        }
                    } else {
                        switch (next.getAttribute("Class")) {
                            case "ImageElement":
                                this.maskImage = new ImageElement();
                                next.setAttribute("Visible", "true");
                                this.maskImage.create(next);
                                if (!doNotHide) {
                                    next.setAttribute("Visible", "false");
                                }
                                this.addChild(this.maskImage);
                                this.mask = this.maskImage;
                                break;
                            default:
                                let shapeElement: ShapeElement = new ShapeElement();
                                shapeElement.create(next);
                                this.mask = shapeElement;
                                break;
                        }
                    }
                }
                next = next.nextElementSibling;
            }

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute(ImageElementAttributes.MASK));
                }
            }
        }

        if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK)) {
            let image = StageController.getTextureByResourceId(node.getAttribute(ImageElementAttributes.BITMAP_MASK));

            if (image != null) {
                this.maskSprite = new PIXI.Sprite(image);
                if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK_X)) {
                    this.maskSprite.position.x = parseFloat(node.getAttribute(ImageElementAttributes.BITMAP_MASK_X));
                }
                if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK_Y)) {
                    this.maskSprite.position.y = parseFloat(node.getAttribute(ImageElementAttributes.BITMAP_MASK_Y));
                }
                if (!node.hasAttribute(ImageElementAttributes.BITMAP_MASK_SHOW)) {
                    this.addChild(this.maskSprite);
                    this.mask = this.maskSprite;
                }
            } else {
                if (DEBUG) {
                    this.log(
                        "ERROR" +
                            this.componentName +
                            " no image with name for mask: " +
                            node.getAttribute(ImageElementAttributes.BITMAP_MASK),
                    );
                }
            }
        }

        if (node.hasAttribute(ImageElementAttributes.TINT)) {
            let spr: PIXI.Sprite;
            if (node.getAttribute(ImageElementAttributes.TINT) === "0") {
                this.tint = null;
            } else if (node.getAttribute(ImageElementAttributes.TINT) === "dynamic") {
                this.tint = parseInt(ImageElement.DynamicTint, 16);
            } else {
                this.tint = parseInt(node.getAttribute(ImageElementAttributes.TINT), 16);
                if (this.children != null && this.children[0] != null && this.children[0] instanceof PIXI.Sprite) {
                    spr = <PIXI.Sprite>this.children[0];
                    spr.tint = this.tint;
                }
            }
        } else {
            //this.tint = 0xFFFFFF;
        }

        this.runPositionTween(node);
        this.runScaleTween(node);
        this.runRotationTween(node);
        this.runAlphaTween(node);
        this.runSkewTween(node);
        this.runTintTween(node);
        this.runTween(node);

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }

        return this;
    }

    /**
     * Runs the alpha tween
     * @method runAlphaTween
     * @param node
     * @returns {boolean}
     */
    public runAlphaTween(node: Element): boolean {
        if (!node.hasAttribute(ImageElementAttributes.ALPHA_TWEEN)) {
            return false;
        }

        Tweens.removeTweens(this, "alpha");

        let alphaTween: Array<number>;

        let alphaTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.ALPHA_TWEEN_POSITION,
            null,
        );

        if (alphaTweenPosition == ImageElementAttributes.ALPHA_TWEEN_POSITION_VALUE.RELATIVE) {
            alphaTween = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.ALPHA_TWEEN)).map(
                (value) => value + this.alpha,
            );
        } else if (alphaTweenPosition == ImageElementAttributes.ALPHA_TWEEN_POSITION_VALUE.SMOOTH) {
            alphaTween = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.ALPHA_TWEEN));
            if (alphaTween.length) {
                alphaTween[0] = this.alpha;
            }
        } else {
            alphaTween = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.ALPHA_TWEEN));
        }

        const alphas: Array<number> = alphaTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(node, ImageElementAttributes.ALPHA_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageElementAttributes.ALPHA_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ImageElementAttributes.ALPHA_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ImageElementAttributes.ALPHA_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageElementAttributes.ALPHA_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ImageElementAttributes.ALPHA_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.ALPHA_TWEEN_KEY_TIMES))
            : null;

        Tweens.alphaTween(this, { alphas, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    /**
     * Runs the position tween
     * @method runPositionTween
     * @param node
     * @returns {boolean}
     */
    public runPositionTween(node: Element): boolean {
        if (
            !node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_X) &&
            !node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_Y)
        ) {
            return false;
        }

        Tweens.removeTweens(this, "position");

        let positionTweenX: Array<number>;
        let positionTweenY: Array<number>;

        let positionTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.POSITION_TWEEN_POSITION,
            null,
        );
        if (positionTweenPosition === ImageElementAttributes.POSITION_TWEEN_POSITION_VALUE.RELATIVE) {
            if (!node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_X)) {
                positionTweenX = [];
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_Y)).map(
                    (value) => {
                        positionTweenX.push(this.position.x);
                        return value + this.position.y;
                    },
                );
            } else if (!node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_Y)) {
                positionTweenY = [];
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_X)).map(
                    (value) => {
                        positionTweenY.push(this.position.y);
                        return value + this.position.x;
                    },
                );
            } else {
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_X)).map(
                    (value) => value + this.position.x,
                );
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_Y)).map(
                    (value) => value + this.position.y,
                );
            }
        } else {
            if (!node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_X)) {
                positionTweenX = [];
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_Y)).map(
                    (value) => {
                        positionTweenX.push(this.position.x);
                        return value;
                    },
                );
            } else if (!node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_Y)) {
                positionTweenY = [];
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_X)).map(
                    (value) => {
                        positionTweenY.push(this.position.y);
                        return value;
                    },
                );
            } else {
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_X));
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_Y));
            }
            if (positionTweenPosition == ImageElementAttributes.POSITION_TWEEN_POSITION_VALUE.SMOOTH) {
                if (positionTweenX.length) {
                    positionTweenX[0] = this.position.x;
                }
                if (positionTweenY.length) {
                    positionTweenY[0] = this.position.y;
                }
            }
        }

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.POSITION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageElementAttributes.POSITION_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ImageElementAttributes.POSITION_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageElementAttributes.POSITION_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_KEY_TIMES))
            : null;

        let bezierType: string;
        let bezierCurviness: number;
        let bezierPoints: Object;
        let bezierObject: Object;
        let bezierAutoRotate: boolean;

        if (node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_BEZIER_POINTS)) {
            bezierType = Attributes.assignStringAttribute(
                node,
                ImageElementAttributes.POSITION_TWEEN_BEZIER_TYPE,
                "thru",
            );
            bezierCurviness = Attributes.assignIntAttribute(
                node,
                ImageElementAttributes.POSITION_TWEEN_BEZIER_CURVINESS,
                1.0,
            );
            bezierPoints = this.formatPoints(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_BEZIER_POINTS));
            bezierAutoRotate = Attributes.assignBooleanAttribute(
                node,
                ImageElementAttributes.POSITION_TWEEN_BEZIER_AUTOROTATE,
                false,
            );

            if (bezierType && bezierCurviness && bezierPoints) {
                bezierObject = {
                    type: bezierType,
                    curviness: bezierCurviness,
                    values: bezierPoints,
                    autoRotate: bezierAutoRotate ? ["x", "y", "rotation", Math.PI / 2, true] : false,
                };
            }
        }

        Tweens.positionTween(this, {
            bezier: bezierObject,
            tweenX,
            tweenY,
            tweenEase,
            tweenDuration,
            tweenDelay,
            tweenLoop,
            keyTimes,
        });
    }

    public runSkewTween(node: Element): boolean {
        if (
            !node.hasAttribute(ImageElementAttributes.SKEW_TWEEN_X) &&
            !node.hasAttribute(ImageElementAttributes.SKEW_TWEEN_Y)
        ) {
            return false;
        }

        Tweens.removeTweens(this, "skew");

        let skewTweenX: Array<number>;
        let skewTweenY: Array<number>;

        let skewTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.SKEW_TWEEN_POSITION,
            null,
        );
        if (skewTweenPosition === ImageElementAttributes.SKEW_TWEEN_POSITION_VALUE.RELATIVE) {
            skewTweenX = node.hasAttribute(ImageElementAttributes.SKEW_TWEEN_X)
                ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.SKEW_TWEEN_X)).map(
                      (value) => Tweens.degrees2radians(value) + this.skew.x,
                  )
                : null;
            skewTweenY = node.hasAttribute(ImageElementAttributes.SKEW_TWEEN_Y)
                ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.SKEW_TWEEN_Y)).map(
                      (value) => Tweens.degrees2radians(value) + this.skew.y,
                  )
                : null;
        } else if (skewTweenPosition == ImageElementAttributes.SKEW_TWEEN_POSITION_VALUE.SMOOTH) {
            skewTweenX = node.hasAttribute(ImageElementAttributes.SKEW_TWEEN_X)
                ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.SKEW_TWEEN_X)).map((value) =>
                      Tweens.degrees2radians(value),
                  )
                : null;
            skewTweenY = node.hasAttribute(ImageElementAttributes.SKEW_TWEEN_Y)
                ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.SKEW_TWEEN_Y)).map((value) =>
                      Tweens.degrees2radians(value),
                  )
                : null;
            if (skewTweenX && skewTweenX.length) {
                skewTweenX[0] = this.skew.x;
            }
            if (skewTweenY && skewTweenY.length) {
                skewTweenY[0] = this.skew.y;
            }
        } else {
            skewTweenX = node.hasAttribute(ImageElementAttributes.SKEW_TWEEN_X)
                ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.SKEW_TWEEN_X)).map((value) =>
                      Tweens.degrees2radians(value),
                  )
                : null;
            skewTweenY = node.hasAttribute(ImageElementAttributes.SKEW_TWEEN_Y)
                ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.SKEW_TWEEN_Y)).map((value) =>
                      Tweens.degrees2radians(value),
                  )
                : null;
        }

        const tweenX: Array<number> = skewTweenX || [];
        const tweenY: Array<number> = skewTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(node, ImageElementAttributes.SKEW_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageElementAttributes.SKEW_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ImageElementAttributes.SKEW_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ImageElementAttributes.SKEW_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageElementAttributes.SKEW_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ImageElementAttributes.SKEW_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.SKEW_TWEEN_KEY_TIMES))
            : null;

        Tweens.skewTween(this, { tweenX, tweenY, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    /**
     * Runs the rotation tween
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runRotationTween(node: Element): boolean {
        if (!node.hasAttribute(ImageElementAttributes.ROTATION_TWEEN)) {
            return false;
        }

        Tweens.removeTweens(this, "rotation");

        let rotationTween: Array<number>;

        let rotationTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.ROTATION_TWEEN_POSITION,
            null,
        );
        if (rotationTweenPosition == ImageElementAttributes.ROTATION_TWEEN_POSITION_VALUE.RELATIVE) {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.ROTATION_TWEEN)).map(
                (value) => value + Tweens.radians2degrees(this.rotation),
            );
        } else if (rotationTweenPosition == ImageElementAttributes.ROTATION_TWEEN_POSITION_VALUE.SMOOTH) {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.ROTATION_TWEEN));
            if (rotationTween.length) {
                rotationTween[0] = Tweens.radians2degrees(this.rotation);
            }
        } else {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.ROTATION_TWEEN));
        }

        const degrees: Array<number> = rotationTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.ROTATION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageElementAttributes.ROTATION_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ImageElementAttributes.ROTATION_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ImageElementAttributes.ROTATION_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageElementAttributes.ROTATION_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ImageElementAttributes.ROTATION_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.ROTATION_TWEEN_KEY_TIMES))
            : null;

        Tweens.rotationTween(this, { degrees, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    public runTintTween(node: Element): boolean {
        if (!node.hasAttribute(ImageElementAttributes.TINT_TWEEN)) {
            return false;
        }

        Tweens.removeTweens(this, "tint");

        let tintTween: Array<number>;

        let tintTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.TINT_TWEEN_POSITION,
            null,
        );
        if (tintTweenPosition == ImageElementAttributes.TINT_TWEEN_POSITION_VALUE.SMOOTH) {
            tintTween = node
                .getAttribute(ImageElementAttributes.TINT_TWEEN)
                .split(";")
                .map((value) => parseInt(value, 16));
            if (tintTween.length) {
                tintTween[0] = this.tint;
            }
        } else {
            tintTween = node
                .getAttribute(ImageElementAttributes.TINT_TWEEN)
                .split(";")
                .map((value) => parseInt(value, 16));
        }

        const tweenValues: Array<number> = tintTween || [];

        const tween: string = "tint";
        const tweenEase: string = Attributes.assignStringAttribute(node, ImageElementAttributes.TINT_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageElementAttributes.TINT_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ImageElementAttributes.TINT_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ImageElementAttributes.TINT_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageElementAttributes.TINT_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ImageElementAttributes.TINT_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.TINT_TWEEN_KEY_TIMES))
            : null;

        Tweens.colorTween(this, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    /**
     * Runs the scale tween
     *
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runScaleTween(node: Element): boolean {
        if (
            !node.hasAttribute(ImageElementAttributes.SCALE_TWEEN_X) &&
            !node.hasAttribute(ImageElementAttributes.SCALE_TWEEN_Y) &&
            !node.hasAttribute(ImageElementAttributes.SCALE_TWEEN)
        ) {
            return false;
        }

        Tweens.removeTweens(this, "scale");

        let scaleTweenX: Array<number>;
        let scaleTweenY: Array<number>;
        let scaleTween: Array<PIXI.Point>;
        let tweenValues: Array<PIXI.Point> = [];

        if (node.hasAttribute(ImageElementAttributes.SCALE_TWEEN_X)) {
            scaleTweenX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.SCALE_TWEEN_X));
        }

        if (node.hasAttribute(ImageElementAttributes.SCALE_TWEEN_Y)) {
            scaleTweenY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.SCALE_TWEEN_Y));
        }

        if (node.hasAttribute(ImageElementAttributes.SCALE_TWEEN)) {
            scaleTween = ImageElement._getPointsFromPointsAttribute(
                node.getAttribute(ImageElementAttributes.SCALE_TWEEN),
            ) as Array<PIXI.Point>;
        }

        let scaleTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.SCALE_TWEEN_POSITION,
            null,
        );

        if (scaleTween && scaleTween.length) {
            if (scaleTweenPosition == ImageElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                tweenValues = scaleTween.map((value: PIXI.Point) => {
                    return {
                        x: value.x + this.scale.x,
                        y: value.y + this.scale.y,
                    } as PIXI.Point;
                });
            } else if (scaleTweenPosition == ImageElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                tweenValues = scaleTween;
                if (tweenValues.length) {
                    tweenValues[0].x = this.scale.x;
                    tweenValues[0].y = this.scale.y;
                }
            } else {
                tweenValues = scaleTween;
            }
        } else if (scaleTweenX && scaleTweenY) {
            if (scaleTweenX.length === scaleTweenY.length) {
                if (scaleTweenPosition == ImageElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                    tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                        return {
                            x: scaleXValue + this.scale.x,
                            y: scaleTweenY[i] + this.scale.y,
                        } as PIXI.Point;
                    });
                } else if (scaleTweenPosition == ImageElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                    tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                        return {
                            x: scaleXValue,
                            y: scaleTweenY[i],
                        } as PIXI.Point;
                    });
                    if (tweenValues.length) {
                        tweenValues[0].x = this.scale.x;
                        tweenValues[0].y = this.scale.y;
                    }
                } else {
                    tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                        return {
                            x: scaleXValue,
                            y: scaleTweenY[i],
                        } as PIXI.Point;
                    });
                }
            }
        } else if (scaleTweenX) {
            if (scaleTweenPosition == ImageElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue + this.scale.x,
                        y: this.scale.y,
                    } as PIXI.Point;
                });
            } else if (scaleTweenPosition == ImageElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: this.scale.y,
                    } as PIXI.Point;
                });
                if (tweenValues.length) {
                    tweenValues[0].x = this.scale.x;
                }
            } else {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: this.scale.y,
                    } as PIXI.Point;
                });
            }
        } else if (scaleTweenY) {
            if (scaleTweenPosition == ImageElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                    return {
                        x: this.scale.x,
                        y: scaleYValue + this.scale.y,
                    } as PIXI.Point;
                });
            } else if (scaleTweenPosition == ImageElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                    return {
                        x: this.scale.x,
                        y: scaleYValue,
                    } as PIXI.Point;
                });
                if (tweenValues.length) {
                    tweenValues[0].y = this.scale.y;
                }
            } else {
                tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                    return {
                        x: this.scale.x,
                        y: scaleYValue,
                    } as PIXI.Point;
                });
            }
        } else {
            return false;
        }

        const tweenEase: string = Attributes.assignStringAttribute(node, ImageElementAttributes.SCALE_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageElementAttributes.SCALE_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ImageElementAttributes.SCALE_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ImageElementAttributes.SCALE_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageElementAttributes.SCALE_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ImageElementAttributes.SCALE_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.SCALE_TWEEN_KEY_TIMES))
            : null;

        Tweens.scaleTween(this, { tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    public runTween(node: Element): boolean {
        if (node.hasAttribute(TweensAttributes.TWEEN)) {
            let tweens: Array<string> = ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN));

            for (let i: number = 0; i < tweens.length; i++) {
                //return if there are no values to tween
                if (!node.hasAttribute(TweensAttributes.TWEEN_VALUES + i)) {
                    return false;
                }

                const tweenValues: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_VALUES + i),
                );
                const tweenDuration: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DURATION + i) || "120",
                );
                const tweenEase: Array<string> = ArrayUtils.stringArray(
                    node.getAttribute(TweensAttributes.TWEEN_EASE + i) || "linear",
                );
                const tweenDelay: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DELAY + i) || "0",
                );
                const tweenLoop: boolean = Attributes.assignBooleanAttribute(
                    node,
                    TweensAttributes.TWEEN_LOOP + i,
                    null,
                );

                let tween: string = tweens[i];

                Tweens.removeTweens(this, tween);

                if (tween == "scale") {
                    tween = "scale.x";
                    tweens[i] = "scale.y";
                    i--;
                }

                Tweens.customTween(this, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
            }
        }

        return false;
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return ImageElement
     */
    clone(): ImageElement {
        let element: ImageElement = new ImageElement();
        element.removeChildren();

        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.anchor = this.anchor;
        element.tint = this.tint;
        element.blendMode = this.blendMode;
        element.name = this.name;
        element._cache = this._cache;
        element._tweenEngine = this._tweenEngine;

        if (this.texture != null) {
            element.texture = this.texture;
        }

        if (element._cache) {
            //element.cacheAsBitmap = true;
        } else {
            //element.cacheAsBitmap = false;
        }

        if (this.maskSprite != null) {
            let elementMask = new PIXI.Sprite((<PIXI.Sprite>this.maskSprite).texture);
            elementMask.position.x = this.maskSprite.position.x;
            elementMask.position.y = this.maskSprite.position.y;

            element.maskSprite = elementMask;
            element.addChild(elementMask);
            element.mask = elementMask;
        }
        if (this.maskImage != null) {
            element.maskImage = this.maskImage.clone();
            element.addChild(element.maskImage);
            element.mask = element.maskImage;
        }

        element.markAsCloned(); // must be called last before return!

        return element;
    }

    /**
     *
     * @param options
     */
    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        Tweens.removeTweens(this, "alpha");
        Tweens.removeTweens(this, "position");
        Tweens.removeTweens(this, "rotation");
        Tweens.removeTweens(this, "scale");
        Tweens.removeTweens(this, "skew");

        this.texture = null;
    }

    getDebugInfo(): string {
        if (DEBUG) {
            let debugInfoFn = this.debugInfo;
            return debugInfoFn ? debugInfoFn.call(this) : `${this.uid}\nName: [${this.name}]`;
        }
        return "";
    }

    debugInfo(): string {
        if (DEBUG) {
            let result = `${this.uid}\nName: [${this.name}]`;
            let imgurl = this.texture && this.texture.baseTexture ? this.texture.baseTexture.imageUrl : "";
            if (!StringUtils.isNullOrEmpty(imgurl)) {
                result += "\nUrl: " + imgurl;
            }
            return result;
        }
        return "";
    }

    /**
     * @property cloned - indicates if element was cloned or not.
     * @returns {boolean}
     */
    public get cloned(): boolean {
        return this._cloned;
    }

    public get destroyed(): boolean {
        return this.__destroyed;
    }

    /**
     * @property uid - unique identifier of element.
     * @returns {string}
     */
    public get uid(): string {
        return this._uid;
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public get componentName(): string {
        return (this as any).__componentName__;
    }

    destroy(options?: PIXI.DestroyOptions | boolean): void {
        this.assertDestroyed("destroy");

        if (Utils.isNullOrUndefined(options)) {
            options = ImageElement._destroyOptions;
        }

        this.internalDestroy(options);

        if (!Utils.isNullOrUndefined(this.children)) {
            super.destroy(options);
        }

        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.destroy(this);
            }
        }

        this.__destroyed = true;
    }

    protected assertDestroyed(method: string): void {
        if (DEBUG) {
            if (this.__destroyed) {
                throw new Error(`${this.componentName}[${this.uid}] was already destroyed, calling method '${method}'`);
            }
        }
    }

    /**
     * Converts string from "points attribute" into array of points. E.g. "(1,6);(5,2);2;(2,4)". Point can be specified as single value for both axex.
     * @method _getPointsFromPointsAttribute
     * @return Array<PIXI.Points>
     */
    private static _getPointsFromPointsAttribute(pointsString: string): Array<{ x: number; y: number }> {
        if (!pointsString) {
            return [];
        }

        return pointsString
            .replace(/\s/g, "")
            .split(";")
            .map((valuesPair: string) =>
                /\(|\)/.test(valuesPair) ? valuesPair.slice(1, -1).split(",") : [valuesPair, valuesPair],
            )
            .map((valuesPair: Array<string>) => {
                return { x: parseFloat(valuesPair[0]), y: parseFloat(valuesPair[1]) };
            });
    }

    /**
     * Marks element as cloned
     */
    protected _markAsCloned(): void {
        this.markAsCloned();
    }

    /**
     * Marks element as cloned
     */
    markAsCloned(): void {
        this._assertDestroyed("markAsCloned()");
        this._cloned = true;

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.clone(this);

                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }
    }

    /**
     *
     * @param method
     * @private
     */
    protected _assertDestroyed(method: string): void {
        if (DEBUG) {
            if (this.__destroyed) {
                throw new Error(`${this.componentName}[${this.uid}] was already destroyed, calling method '${method}'`);
            }
        }
    }

    /**
     * @method removeAndDestroyChildren - recursivelly destroy and remove whole children tree.
     */
    removeAndDestroyChildren(): void {
        this._removeAndDestroyChildren();
    }

    /**
     * @method removeAndDestroyChildren - recursivelly destroy and remove whole children tree.
     */
    protected _removeAndDestroyChildren(): void {
        this._assertDestroyed("_removeAndDestroyChildren");

        let destroyOptions: PIXI.DestroyOptions = {
            children: true,
        };

        if (this.children != null && this.children.length > 0) {
            for (let i = this.children.length - 1; i >= 0; i--) {
                if (this.children[i] != null) {
                    (this.children[i] as any).destroy(destroyOptions);
                }
            }
        }

        this.removeChildren();
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public degrees2radians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     * Important! The WebGL renderer only supports the NORMAL, ADD, MULTIPLY and SCREEN blend modes.
     * Anything else will silently act like NORMAL.
     *
     * @param mode
     * @returns {number}
     */
    public getBlendModeByString(mode: string): number {
        mode = mode.toString();
        switch (mode) {
            case "0":
            case "normal":
                return PIXI.BLEND_MODES.NORMAL;
            case "1":
            case "add":
                return PIXI.BLEND_MODES.ADD;
            case "2":
            case "multiply":
                return PIXI.BLEND_MODES.MULTIPLY;
            case "3":
            case "screen":
                return PIXI.BLEND_MODES.SCREEN;
            case "4":
            case "overlay":
                return PIXI.BLEND_MODES.OVERLAY;
            case "5":
            case "darken":
                return PIXI.BLEND_MODES.DARKEN;
            case "6":
            case "lighten":
                return PIXI.BLEND_MODES.LIGHTEN;
            case "7":
            case "color_dodge":
                return PIXI.BLEND_MODES.COLOR_DODGE;
            case "8":
            case "color_burn":
                return PIXI.BLEND_MODES.COLOR_BURN;
            case "9":
            case "hard_light":
                return PIXI.BLEND_MODES.HARD_LIGHT;
            case "10":
            case "soft_light":
                return PIXI.BLEND_MODES.SOFT_LIGHT;
            case "11":
            case "difference":
                return PIXI.BLEND_MODES.DIFFERENCE;
            case "12":
            case "exclusion":
                return PIXI.BLEND_MODES.EXCLUSION;
            case "13":
            case "hue":
                return PIXI.BLEND_MODES.HUE;
            case "14":
            case "saturation":
                return PIXI.BLEND_MODES.SATURATION;
            case "15":
            case "color":
                return PIXI.BLEND_MODES.COLOR;
            case "16":
            case "luminosity":
                return PIXI.BLEND_MODES.LUMINOSITY;
            default:
                if (DEBUG) {
                    this.log("ERROR Unknown blending mode " + mode + ". Used default mode NORMAL.");
                }
                return PIXI.BLEND_MODES.NORMAL;
        }
    }

    /**
     * Prints output on screen inside element with specified debug content
     */
    protected _printDebugInfo(text: any, node?: Element) {
        if (DEBUG) {
            if (this.getChildByName(ImageElementAttributes.DEBUG_INFO) != null) {
                this.removeChild(this.getChildByName(ImageElementAttributes.DEBUG_INFO));
            }
            if (
                node == undefined ||
                (node.hasAttribute(ImageElementAttributes.DEBUG_INFO) &&
                    node.getAttribute(ImageElementAttributes.DEBUG_INFO) === "true")
            ) {
                // test
                /*let border = new ShapeElement();
                 border.beginFill(0xFFFFFF, 0);
                 border.lineStyle(2, 0xff0000);
                 border.drawRect(0, 0, this._textures[0].width, this._textures[0].height);
                 this.addChild(border);*/

                let textInfo = new TextElement();
                textInfo.name = ImageElementAttributes.DEBUG_INFO;
                textInfo.x = textInfo.y = 0;
                textInfo.style.fill = 0xff0000;
                textInfo.style.fontSize = 10;
                textInfo.text = JSON.stringify(text, null, 4);

                this.addChild(textInfo);
            }
        }
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Log error into console
     * @param s
     */
    protected logError(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.errorGroup(compName, s);
        }
    }

    /**
     *
     * @param {string} value
     * @param {string} id
     * @param {boolean} useGlobal
     * @returns {number}
     */
    private formatPoint(value: string, id: string, useGlobal: boolean = false): number {
        let result: number;
        if (value == "c") {
            result = this[id];
        } else {
            if (useGlobal) {
                result = this[id] + (parseInt(value) - this.getGlobalPosition()[id]);
            } else {
                result = parseInt(value);
            }
        }

        return result;
    }

    /**
     *
     * @param {string} points
     * @param {boolean} useGlobal
     * @returns {Array<PIXI.Point>}
     */
    private formatPoints(points: string, useGlobal: boolean = false): Array<Object> {
        let result: Array<Object> = [];
        let regex = /\[(.*?)\]/g;
        let data = points.match(regex);

        for (let i: number = 0; i < data.length; i++) {
            let pointArray = data[i].substring(1, data[i].length - 1).split(",");

            if (pointArray.length == 2) {
                let x = this.formatPoint(pointArray[0], "x", useGlobal);
                let y = this.formatPoint(pointArray[1], "y", useGlobal);

                //let point: PIXI.Point = new PIXI.Point(x, y);
                let objectPoint: Object = { x: x, y: y };
                result.push(objectPoint);
            }
        }

        return result;
    }
}

export const ImageElementAttributes = {
    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",
    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",
    ALPHA_TWEEN_LOOP: "AlphaTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    ALPHA_TWEEN_KEY_TIMES: "AlphaTweenKeyTimes",
    ALPHA_TWEEN_POSITION: "AlphaTweenPosition",
    ALPHA_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute" /* values are taken as they are */,
        RELATIVE: "relative" /* values are offset by initial value */,
        SMOOTH: "smooth" /* first value is replaced with initial value */,
    },
    ANCHOR: "Anchor",
    /**
     * Target animation from where is specific frame selected. Attribute Frame="?" needed.
     */
    ANIMATION: "Animation",
    /**
     * A ImageElement instance that defines a mask for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property BitmapMask
     * @type string
     * @default null
     */
    BITMAP_MASK: "BitmapMask",
    BITMAP_MASK_SHOW: "BitmapMaskShow",
    BITMAP_MASK_X: "BitmapMaskX",
    BITMAP_MASK_Y: "BitmapMaskY",
    /*
     * Blend mode for animation.
     * Bleding modes:
     *   0 = NORMAL;
     *   1 = ADD;
     *   2 = MULTIPLY;
     *   3 = SCREEN;
     *   4 = OVERLAY;
     *   5 = DARKEN;
     *   6 = LIGHTEN;
     *   7 = COLOR_DODGE;
     *   8 = COLOR_BURN;
     *   9 = HARD_LIGHT;
     *   10 = SOFT_LIGHT;
     *   11 = DIFFERENCE;
     *   12 = EXCLUSION;
     *   13 = HUE;
     *   14 = SATURATION;
     *   15 = COLOR;
     *   16 = LUMINOSITY;
     * @property BlendMode
     * @type number
     * @default null
     */
    BLEND_MODE: "BlendMode",
    /*
     * If true cache image as bitmap.
     * @property Cache
     * @type boolean
     * @default null
     */
    CACHE: "Cache",
    DEBUG_INFO: "DebugInfo",
    DEBUG: "Debug",

    FRAME: "Frame",

    FROM_FRAME: "FromFrame",
    /**
     * Reference to image from resources.
     * @property Image
     * @type string
     * @default 0
     */
    IMAGE: "Image",
    /*
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type ShapeElement
     * @default null
     */
    MASK: "Mask",

    CLEAR_MASK: "ClearMask",
    SHOW_MASK: "ShowMask",
    /*
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /*
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_DELAY: "PositionTweenDelay",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
        SMOOTH: "smooth",
    },
    POSITION_TWEEN_LOOP: "PositionTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    POSITION_TWEEN_KEY_TIMES: "PositionTweenKeyTimes",
    /**
     * Bezier's "type" of curve
     * VALUES:
     *      thru
     *      soft
     *      quadratic
     *      cubic
     * EXAMPLE:
     *      PositionTweenBezierType="thru"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540;540" '
     * @property PositionTweenBezierType
     * @default thru
     */
    POSITION_TWEEN_BEZIER_TYPE: "PositionTweenBezierType",
    /**
     * the tension on the Bezier
     * VALUES:
     *      0 has no curviness (straight lines),
     *      1 is normal curviness,
     *      2 is twice the normal curviness,
     *      etc. Use any number, not just integers
     * EXAMPLE:
     *      PositionTweenBezierCurviness="1.25"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540;540" '
     * @property PositionTweenBezierCurviness
     * @default 1.0
     */
    POSITION_TWEEN_BEZIER_CURVINESS: "PositionTweenBezierCurviness",
    /**
     * Array points, pairs of X,Y coords for movement along the bezier curve
     * EXAMPLE:
     *                                   x   y     x  y    x   y
     *      PositionTweenBezierPoints="[100,250];[300,0];[500,400]"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540;540" '
     * @property PositionTweenBezierPoints
     * @default null
     */
    POSITION_TWEEN_BEZIER_POINTS: "PositionTweenBezierPoints",
    /**
     * Auto rotation object on Bezier
     * VALUES:
     *      true
     *      false
     * EXAMPLE:
     *      PositionTweenBezierAutoRotate="true"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540;540" '
     * @property PositionTweenBezierAutoRotate
     * @default false
     */
    POSITION_TWEEN_BEZIER_AUTOROTATE: "PositionTweenBezierAutoRotate",
    /*
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    ROTATION_TWEEN: "RotationTween",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",
    ROTATION_TWEEN_LOOP: "RotationTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    ROTATION_TWEEN_KEY_TIMES: "RotationTweenKeyTimes",
    ROTATION_TWEEN_POSITION: "RotationTweenPosition",
    ROTATION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute" /* values are taken as they are */,
        RELATIVE: "relative" /* values are offset by initial value */,
        SMOOTH: "smooth" /* first value is replaced with initial value */,
    },
    /**
     *
     */
    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_X: "ScaleTweenX",
    SCALE_TWEEN_Y: "ScaleTweenY",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_DELAY: "ScaleTweenDelay",
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    SCALE_TWEEN_LOOP: "ScaleTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    SCALE_TWEEN_KEY_TIMES: "ScaleTweenKeyTimes",
    SCALE_TWEEN_POSITION: "ScaleTweenPosition",
    SCALE_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute" /* values are taken as they are */,
        RELATIVE: "relative" /* values are offset by initial value */,
        SMOOTH: "smooth" /* first value is replaced with initial value */,
    },
    /*
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    SCALE_X: "ScaleX",
    /*
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",

    SKEW_X: "SkewX",
    SKEW_Y: "SkewY",

    SKEW_TWEEN_X: "SkewTweenX",
    SKEW_TWEEN_Y: "SkewTweenY",
    SKEW_TWEEN_EASE: "SkewTweenEase",
    SKEW_TWEEN_DURATION: "SkewTweenDuration",
    SKEW_TWEEN_DELAY: "SkewTweenDelay",
    SKEW_TWEEN_POSITION: "SkewTweenPosition",
    SKEW_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
        SMOOTH: "smooth",
    },
    SKEW_TWEEN_LOOP: "SkewTweenLoop",
    SKEW_TWEEN_KEY_TIMES: "SkewTweenKeyTimes",

    /*
     * Random rotation in degrees for this display object.
     * @property StartRotation
     * @type Number
     * @default null
     */
    START_ROTATION: "StartRotation",
    /*
     * Tint of image. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",

    TINT_TWEEN: "TintTween",
    TINT_TWEEN_EASE: "TintTweenTease",
    TINT_TWEEN_DURATION: "TintTweenDuration",
    TINT_TWEEN_DELAY: "TintTweenDelay",
    TINT_TWEEN_POSITION: "TintTweenPosition",
    TINT_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        SMOOTH: "smooth",
    },
    TINT_TWEEN_LOOP: "TintTweenLoop",
    TINT_TWEEN_KEY_TIMES: "TintTweenKeyTimes",

    TWEEN_ENGINE: "TweenEngine",
    /*
     * Indicates currently running tweens should not be interrupted during updateByNode call
     * @property TweenPersistent
     * @type Boolean
     * @default false
     */
    TWEEN_PERSISTENT: "TweenPersistent",
    VISIBLE: "Visible",
    /*
     * X (horizontal) position of the display object, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * Y (vertical) position of the display object, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/ImageElement.ts
 **/